import { useContext, useEffect } from 'react'
import { Button, Text, VStack, CloseButton } from '@chakra-ui/react'
import { colors } from '../../../../ui-kit'

import { useCallback, useMemo } from 'react'
import { Formik } from 'formik'
import { Box } from '@chakra-ui/react'
import * as Yup from 'yup'
import { isNull, isUndefined } from 'lodash'
import MetaDataFields from './MetaDataFields'
import {
  formatCommonSettingExtensions,
  formatCustomMetadata,
  formatExtensions,
  getCmFieldValidatorMap,
  validateCmField,
  validateCommonSettingCustomCoordinates,
  validateCustomMetadata,
} from './uploadUtils'
import { useTranslation } from 'react-i18next'
import { UserContext } from '../../../providers'

const AdvancePopup = ({
  isOpen,
  onClose,
  onSubmit,
  commonSettings,
  pathPolicy,
  cmFields,
}) => {
  const { user } = useContext(UserContext)
  const validationSchema = Yup.object().shape({
    customCoordinatesOb: Yup.object()
      .shape({
        ['x-coordinate']: Yup.number().typeError(
          'x-coordinate must be a number',
        ),
        ['y-coordinate']: Yup.number().typeError(
          'y-coordinate must be a number',
        ),
        width: Yup.number().typeError('Width must be a number'),
        height: Yup.number().typeError('Height must be a number'),
      })
      .optional(),
  })

  const { t } = useTranslation()

  const updatedCmField = useMemo(() => {
    return cmFields?.map(data => ({
      ...data,
      name: `IK_TEMP_CUSTOM_METADATA_${data.name}`,
      originalName: data.name,
    }))
  }, [cmFields])

  const cmFieldValidatorMap = getCmFieldValidatorMap(updatedCmField)

  // Prevent Uppy Url plugin's root paste handler from triggering when editing common settings
  useEffect((): (() => void) | undefined => {
    if (!isOpen) {
      return
    }

    const formEl = document.getElementById('ik-uppy-common-settings-form')
    if (!formEl) {
      return
    }

    const stopPropagationOnPaste = (e: Event): void => {
      e.stopPropagation()
    }
    const stopPropagationOnDrop = (e: Event): void => {
      e.stopPropagation()
    }

    formEl.addEventListener('paste', stopPropagationOnPaste, true)
    formEl.addEventListener('drop', stopPropagationOnDrop, true)

    return () => {
      formEl.removeEventListener('paste', stopPropagationOnPaste, true)
      formEl.removeEventListener('drop', stopPropagationOnDrop, true)
    }
  }, [isOpen])

  useEffect(() => {
    const insertedElement = document.getElementById('unique') as HTMLElement
    if (insertedElement) {
      insertedElement.style.display = 'none'
    }
  }, [])

  const detectCmFields = useCallback(
    values => {
      const cmFieldsName = updatedCmField.map(data => data.name)
      const customMetadata = Object.keys(values)
        .filter(fieldName => cmFieldsName.includes(fieldName))
        .reduce((ob, data) => ({ ...ob, [data]: values[data] }), {})
      const tempCustomMetadata = {}
      Object.keys(customMetadata).forEach(key => {
        if (
          key.split('IK_TEMP_CUSTOM_METADATA_')?.length &&
          key.split('IK_TEMP_CUSTOM_METADATA_')[1]
        )
          tempCustomMetadata[key.split('IK_TEMP_CUSTOM_METADATA_')[1]] =
            customMetadata[key]
      })
      return JSON.stringify(tempCustomMetadata)
    },
    [updatedCmField],
  )

  const initialValues = useMemo(() => {
    if (commonSettings && user) {
      let fileObject = commonSettings

      if (user?.customMetadata && Array.isArray(user?.customMetadata)) {
        fileObject = {
          ...fileObject,
          customMetadata: user?.customMetadata?.reduce(
            (ob, data) => ({ ...ob, ...data }),
            {},
          ),
        }
      }

      // setting custom metadata values
      if (updatedCmField?.length) {
        if (pathPolicy) {
          updatedCmField.forEach(cmData => {
            if (validateCustomMetadata(commonSettings, cmData.originalName)) {
              fileObject = formatCustomMetadata(
                fileObject,
                commonSettings,
                cmData.name,
                cmData.originalName
              );
            } 
            
            if (
              cmData.schema?.defaultValue !== undefined &&
              cmData.schema?.defaultValue !== null &&
              (fileObject[cmData.name] === undefined || fileObject[cmData.name] === null)
            ) {
              fileObject = {
                ...fileObject,
                [cmData.name]: cmData.schema.defaultValue,
              };
            }
          });
        } else {
          updatedCmField.forEach(cmData => {
            if (validateCustomMetadata(commonSettings, cmData?.originalName))
              fileObject = formatCustomMetadata(
                fileObject,
                commonSettings,
                cmData?.name,
                cmData?.originalName,
              )
            else if (
              !(
                commonSettings?.customMetadata &&
                typeof commonSettings.customMetadata === 'string' &&
                Object.keys(JSON.parse(commonSettings.customMetadata)).length &&
                isNull(
                  JSON.parse(commonSettings?.customMetadata)[
                  cmData?.originalName
                  ],
                )
              )
            ) {
              if (
                user?.customMetadata &&
                Array.isArray(user.customMetadata) &&
                Object.keys(fileObject.customMetadata)?.length &&
                !(
                  isUndefined(fileObject.customMetadata[cmData?.originalName]) ||
                  isNull(fileObject.customMetadata[cmData?.originalName])
                )
              )
                fileObject = {
                  ...fileObject,
                  [cmData?.name]: fileObject.customMetadata[cmData?.originalName],
                }
            }
            if (
              cmData.schema?.defaultValue !== undefined &&
              cmData.schema?.defaultValue !== null &&
              (fileObject[cmData.name] === undefined || fileObject[cmData.name] === null)
            ) {
              fileObject = {
                ...fileObject,
                [cmData.name]: cmData.schema.defaultValue,
              };
            }
          });
        }
      }

      // setting custom coordinates
      if (commonSettings?.customCoordinatesOb)
        fileObject = {
          ...fileObject,
          customCoordinatesOb: commonSettings?.customCoordinatesOb,
        }
      else if (validateCommonSettingCustomCoordinates(user?.customCoordinates))
        fileObject = {
          ...fileObject,
          customCoordinatesOb: user?.customCoordinates,
        }

      // setting extensions
      if (commonSettings?.extensionsData)
        fileObject = formatCommonSettingExtensions(fileObject, commonSettings)
      else if (user?.extensions?.length)
        fileObject = formatExtensions(fileObject, user?.extensions)

      // setting saved extensions
      // Check if savedExtensions exists in commonSettings (even if empty array)
      if ('savedExtensions' in commonSettings) {
        fileObject = {
          ...fileObject,
          savedExtensions: commonSettings.savedExtensions || [],
        }
      } else if (user?.savedExtensions?.length) {
        // Apply global settings as default only if commonSettings doesn't have savedExtensions
        fileObject = {
          ...fileObject,
          savedExtensions: user.savedExtensions,
        }
      } else {
        fileObject = {
          ...fileObject,
          savedExtensions: [],
        }
      }

      // setting webhook url
      if (
        !(
          isUndefined(commonSettings?.webhookUrl) ||
          isNull(commonSettings?.webhookUrl)
        )
      )
        fileObject = {
          ...fileObject,
          webhookUrl: commonSettings.webhookUrl,
        }
      else
        fileObject = {
          ...fileObject,
          webhookUrl: '',
        }

      // setting unique name
      if (
        !(
          isUndefined(commonSettings?.useUniqueFileName) ||
          isNull(commonSettings?.useUniqueFileName)
        )
      )
        fileObject = {
          ...fileObject,
          useUniqueFileName: commonSettings?.useUniqueFileName,
        }
      else if (
        !(
          isUndefined(user?.uniqueFileNameOnUpload) ||
          isNull(user?.uniqueFileNameOnUpload)
        )
      )
        fileObject = {
          ...fileObject,
          useUniqueFileName:
            user?.uniqueFileNameOnUpload === 'true' ? true : false,
        }

      // setting isPrivateFile
      if (
        !(
          isUndefined(commonSettings?.isPrivateFile) ||
          isNull(commonSettings?.isPrivateFile)
        )
      )
        fileObject = {
          ...fileObject,
          isPrivateFile: commonSettings?.isPrivateFile,
        }
      else if (
        !(isUndefined(user?.isPrivateFile) || isNull(user?.isPrivateFile))
      )
        fileObject = {
          ...fileObject,
          isPrivateFile: user?.isPrivateFile === 'true' ? true : false,
        }

      // setting isPublished
      if (
        !(
          isUndefined(commonSettings?.isPublished) ||
          isNull(commonSettings?.isPublished)
        )
      ) {
        fileObject = {
          ...fileObject,
          isPublished: commonSettings?.isPublished,
        }
      } else if (
        !(
          isUndefined(user?.publishFileOnUpload) ||
          isNull(user?.publishFileOnUpload)
        )
      ) {
        fileObject = {
          ...fileObject,
          isPublished: user?.publishFileOnUpload === 'true' ? true : false,
        }
      }

      // setting overwriteAITags
      if (
        !(
          isUndefined(commonSettings?.overwriteAITags) ||
          isNull(commonSettings?.overwriteAITags)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteAITags: commonSettings?.overwriteAITags,
        }
      else if (
        !(isUndefined(user?.overwriteAITags) || isNull(user?.overwriteAITags))
      )
        fileObject = {
          ...fileObject,
          overwriteAITags: user?.overwriteAITags === 'true' ? true : false,
        }

      // setting overwriteFile
      if (
        !(
          isUndefined(commonSettings?.overwriteFile) ||
          isNull(commonSettings?.overwriteFile)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteFile: commonSettings?.overwriteFile,
        }
      else if (
        !(isUndefined(user?.overwriteFile) || isNull(user?.overwriteFile))
      )
        fileObject = {
          ...fileObject,
          overwriteFile: user?.overwriteFile === 'true' ? true : false,
        }

      // setting overwriteTags
      if (
        !(
          isUndefined(commonSettings?.overwriteTags) ||
          isNull(commonSettings?.overwriteTags)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteTags: commonSettings?.overwriteTags,
        }
      else if (
        !(isUndefined(user?.overwriteTags) || isNull(user?.overwriteTags))
      )
        fileObject = {
          ...fileObject,
          overwriteTags: user?.overwriteTags === 'true' ? true : false,
        }

      // setting overwriteDescription
      if (
        !(
          isUndefined(commonSettings?.overwriteDescription) ||
          isNull(commonSettings?.overwriteDescription)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteDescription: commonSettings?.overwriteDescription,
        }
      else if (
        !(isUndefined(user?.overwriteDescription) || isNull(user?.overwriteDescription))
      )
        fileObject = {
          ...fileObject,
          overwriteDescription: user?.overwriteDescription === 'true' ? true : false,
        }

      // setting overwriteCustomMetadata
      if (pathPolicy) {
        fileObject = {
          ...fileObject,
          overwriteCustomMetadata: true,
        }
      } else {
        if (
          !(
            isUndefined(commonSettings?.overwriteCustomMetadata) ||
            isNull(commonSettings?.overwriteCustomMetadata)
          )
        )
          fileObject = {
            ...fileObject,
            overwriteCustomMetadata: commonSettings?.overwriteCustomMetadata,
          }
        else if (
          !(
            isUndefined(user?.overwriteCustomMetadata) ||
            isNull(user?.overwriteCustomMetadata)
          )
        )
          fileObject = {
            ...fileObject,
            overwriteCustomMetadata:
              user?.overwriteCustomMetadata === 'true' ? true : false,
          }
      }

      // setting preTransformation
      if (
        !(
          isUndefined(commonSettings?.preTransformation) ||
          isNull(commonSettings?.preTransformation)
        )
      ) {
        fileObject = {
          ...fileObject,
          preTransformation: commonSettings?.preTransformation,
        }
      } else if (
        !(
          isUndefined(user?.preTransformation) ||
          isNull(user?.preTransformation)
        )
      ) {
        fileObject = {
          ...fileObject,
          preTransformation: user?.preTransformation,
        }
      }

      // setting postTransformation
      if (
        !(
          isUndefined(commonSettings?.postTransformation) ||
          isNull(commonSettings?.postTransformation)
        )
      ) {
        fileObject = {
          ...fileObject,
          postTransformation: commonSettings?.postTransformation,
        }
      } else if (!isNull(commonSettings?.postTransformation)) {
        if (
          !(
            isUndefined(user?.postTransformation) ||
            isNull(user?.postTransformation)
          )
        ) {
          fileObject = {
            ...fileObject,
            postTransformation: user?.postTransformation,
          }
        } else {
          fileObject = {
            ...fileObject,
            postTransformation: [],
          }
        }
      }

      if (pathPolicy) {
        fileObject = {
          ...fileObject,
          customMetadataSelect: "cm_form",
        }
      } else {
        if (
          !(
            isUndefined(commonSettings?.customMetadataSelect) ||
            isNull(commonSettings?.customMetadataSelect)
          )
        ){
          fileObject = {
            ...fileObject,
            customMetadataSelect: commonSettings?.customMetadataSelect,
          }
        } else {
          fileObject = {
            ...fileObject,
            customMetadataSelect: "cm_default",
          }
        }
      }

      return fileObject
    }
  }, [commonSettings, updatedCmField, user])

  if (isOpen)
    return (
      <Box
        position="absolute"
        width="100%"
        height="100%"
        bottom="0"
        zIndex="1002"
        borderRadius="5px"
      >
        <Box
          position="absolute"
          width="100%"
          height="70%"
          bottom="0"
          background="#fff"
          zIndex="1002"
          borderRadius="5px"
          boxShadow="0 0 0 50vmax rgba(0,0,0,.5)"
          display="flex"
          flexDirection="column"
        >
          <Box
            display="flex"
            width={'100%'}
            justifyContent="space-between"
            padding="1rem"
            alignItems="center"
            backgroundColor="#fafafa"
            borderBottom="1px solid #eaeaea"
          >
            <Text paddingLeft="1rem" color={colors.appBlue}>
              {t('common_settings', 'Common settings')}
            </Text>
            <CloseButton onClick={onClose} />
          </Box>
          <Formik
            initialValues={initialValues}
            onSubmit={values =>
              onSubmit({
                ...values,
                customMetadata: detectCmFields(values),
              })
            }
            validate={formValues =>
              validateCmField(
                formValues,
                updatedCmField,
                cmFieldValidatorMap,
                false,
              )
            }
            validationSchema={validationSchema}
            enableReinitialize={true}
          >
            {({ handleChange, values, submitForm, dirty, isValid }) => (
              <>
                <VStack
                  spacing="1em"
                  direction="column"
                  alignItems="flex-start"
                  height={'100%'}
                  justifyContent="space-between"
                  overflowY="auto"
                  padding="1rem 2rem"
                  id="ik-uppy-common-settings-form"
                >
                  <MetaDataFields
                    file={null}
                    values={values}
                    handleChange={handleChange}
                    isCommonSettings={true}
                    cmFields={updatedCmField}
                    fileVersion={false}
                    pathPolicy={pathPolicy}
                  />
                </VStack>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  width="100%"
                  padding="1rem 2rem"
                  backgroundColor="#fafafa"
                  borderTop="1px solid #eaeaea"
                >
                  <Button  type="submit" onClick={submitForm} isDisabled={!isValid || !dirty}>
                    {t('save', 'Save')}
                  </Button>
                </Box>
              </>
            )}
          </Formik>
        </Box>
      </Box>
    )
  return <></>
}

export default AdvancePopup
